/**
 * Team Leave Balances Hook
 *
 * Loads leave balances for every member of the current manager's team, for the
 * "Team Leave Balance" tab in the Manager Portal's Leave Details section.
 *
 * The team roster comes from `useTeamMembers` (EssWorkerManagerRelations +
 * EmployeesV2 + PositionsV2). For each member we fetch their balances from
 * `/data/EssLeaveBalances` filtered by PersonnelNumber
 * (leaveService.getLeaveBalancesByEmployee).
 *
 * Returns:
 *   - rows: Array<{ memberId, name, role, status, personnelNumber,
 *                   leaveType, balance, unit }>   // one summary row per member
 *   - balancesByMember: Record<personnelNumber, Array<balance breakdown>>
 *   - isLoading: boolean
 *   - error: Error | null
 *
 * Each balance breakdown item is normalised to the shape the modal renders:
 *   { BalanceType, BalanceRemaining, AccrualStartDate, unit }
 *
 * NOTE on field names: the live EssLeaveBalances entity exposes the leave type
 * as `LeaveTypeId`, the remaining amount as `BalanceAvailable`, and accrual
 * info as `AccrualRateDescription` (see ManagerPersonalLeave.jsx /
 * EmployeeLeaveManagement.jsx, which render the same API). The unit (Days vs
 * Hours) is not on the balance record — it comes from the LeaveTypes lookup
 * (`LeaveAmountUnit` keyed by `Type`), defaulting to Days.
 */
import { useQuery } from "react-query";
import { leaveService } from "../../api/services";
import useTeamMembers from "./useTeamMembers";

/**
 * Pick the headline balance to show on the summary row: prefer Annual Leave,
 * otherwise fall back to the first balance record.
 */
const getPrimaryBalance = (balances = []) => {
  if (!balances.length) return null;
  return (
    balances.find((b) => /annual/i.test(b.BalanceType || "")) || balances[0]
  );
};

/**
 * Normalise a raw EssLeaveBalances record into the shape the modal renders.
 * @param {Object} raw - Raw EssLeaveBalances record
 * @param {Object} unitMap - Map of LeaveTypeId -> LeaveAmountUnit ('Hours'|'Days')
 */
const mapBalance = (raw, unitMap = {}) => {
  const leaveType = raw.LeaveTypeId || "Leave";
  const amountUnit = unitMap[leaveType];
  return {
    BalanceType: leaveType,
    BalanceRemaining: raw.BalanceAvailable != null ? raw.BalanceAvailable : 0,
    AccrualStartDate: raw.AccrualRateDescription || null,
    unit: amountUnit === "Hours" ? "hours" : "days",
  };
};

export default function useTeamLeaveBalances() {
  const {
    members,
    isLoading: membersLoading,
    error: membersError,
  } = useTeamMembers();

  // Stable key from the personnel numbers so the query re-runs when the roster
  // changes but not on every render.
  const personnelKey = members.map((m) => m.personnelNumber).join(",");

  const {
    data,
    isLoading: balancesLoading,
    error: balancesError,
  } = useQuery(
    ["teamLeaveBalances", personnelKey],
    async () => {
      console.log(
        "📊 Fetching leave balances for",
        members.length,
        "team member(s)"
      );

      // Build a LeaveTypeId -> unit map once so we can label Days vs Hours.
      // Optional: if it fails we fall back to Days, matching the employee view.
      let unitMap = {};
      try {
        const leaveTypesResponse = await leaveService.getLeaveTypes();
        unitMap = (leaveTypesResponse.data?.value || []).reduce((acc, lt) => {
          acc[lt.Type] = lt.LeaveAmountUnit;
          return acc;
        }, {});
      } catch {
        // unit map is optional; continue with defaults
      }

      const balancesByMember = {};
      const rows = [];

      await Promise.all(
        members.map(async (member) => {
          try {
            const response = await leaveService.getLeaveBalancesByEmployee(
              member.personnelNumber
            );
            // Only show balances the employee hasn't opted to hide.
            const raw = (response.data?.value || []).filter(
              (b) => b.HideLeaveBalances !== "Yes"
            );
            const balances = raw.map((b) => mapBalance(b, unitMap));
            balancesByMember[member.personnelNumber] = balances;

            const primary = getPrimaryBalance(balances);
            rows.push({
              memberId: member.id,
              personnelNumber: member.personnelNumber,
              name: member.name,
              role: member.role,
              status: member.status,
              leaveType: primary?.BalanceType || "—",
              balance: primary?.BalanceRemaining ?? 0,
              unit: primary?.unit || "days",
            });
          } catch (err) {
            console.error(
              `❌ Failed to fetch leave balances for ${member.personnelNumber}:`,
              err
            );
            balancesByMember[member.personnelNumber] = [];
            // Still show the member with a zeroed row so they aren't dropped.
            rows.push({
              memberId: member.id,
              personnelNumber: member.personnelNumber,
              name: member.name,
              role: member.role,
              status: member.status,
              leaveType: "—",
              balance: 0,
              unit: "days",
            });
          }
        })
      );

      // Preserve the roster order (Promise.all resolves out of order).
      rows.sort(
        (a, b) =>
          members.findIndex((m) => m.personnelNumber === a.personnelNumber) -
          members.findIndex((m) => m.personnelNumber === b.personnelNumber)
      );

      return { rows, balancesByMember };
    },
    {
      enabled: members.length > 0,
      retry: 2,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    rows: data?.rows ?? [],
    balancesByMember: data?.balancesByMember ?? {},
    isLoading: membersLoading || (members.length > 0 && balancesLoading),
    error: membersError || balancesError || null,
  };
}
